// manufacturing/ManufacturingReports.jsx - Complete Fixed Version
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import { toast } from "react-toastify";
import Select from 'react-select';
import {
    Search, RotateCcw, ChevronUp,
    Package, DollarSign, TrendingUp, AlertTriangle,
    BarChart2, PieChart
} from 'feather-icons-react/build/IconComponents';
import { manufacturingActions } from "../../core/action/manufacturingAction";
//import ImageWithBasePath from '../../core/img/imagewithbasebath';

const ManufacturingReports = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State management
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState({
        total_productions: 0,
        total_raw_material_cost: 0,
        total_production_cost: 0,
        total_waste_cost: 0,
        total_units_produced: 0,
        total_waste_quantity: 0,
        waste_percentage: 0
    });
    const [productionReport, setProductionReport] = useState([]);
    const [wasteReport, setWasteReport] = useState([]);
    const [activeTab, setActiveTab] = useState('summary');

    // Filters
    const [filters, setFilters] = useState({
        from_date: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0],
        production_id: ''
    });

    // Production options for filtering
    const [productionOptions, setProductionOptions] = useState([]);

    // Check permissions
    const canView = manufacturingActions.hasManufacturingPermission('view-manufacturing-reports');

    // Load all data
    const loadAllData = useCallback(async () => {
        if (!canView) return;

        setLoading(true);
        try {
            await Promise.all([
                loadSummary(),
                loadProductionReport(),
                loadWasteReport()
            ]);
        } catch (error) {
            console.error('Error loading reports:', error);
            toast.error('Failed to load reports');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    // Load summary data
    const loadSummary = async () => {
        try {
            const result = await manufacturingActions.getManufacturingSummary(filters);
            if (result.success && result.data) {
                setSummary({
                    total_productions: result.data.total_productions || 0,
                    total_raw_material_cost: result.data.total_raw_material_cost || 0,
                    total_production_cost: result.data.total_production_cost || 0,
                    total_waste_cost: result.data.total_waste_cost || 0,
                    total_units_produced: result.data.total_units_produced || 0,
                    total_waste_quantity: result.data.total_waste_quantity || 0,
                    waste_percentage: result.data.waste_percentage || 0
                });
            }
        } catch (error) {
            console.error('Error loading summary:', error);
        }
    };

    // Load production report
    const loadProductionReport = async () => {
        try {
            const result = await manufacturingActions.getProductionReport(filters);
            if (result.success) {
                setProductionReport(result.data || []);
            }
        } catch (error) {
            console.error('Error loading production report:', error);
        }
    };

    // Load waste report
    const loadWasteReport = async () => {
        try {
            const result = await manufacturingActions.getWasteReport(filters);
            if (result.success) {
                setWasteReport(result.data || []);
            }
        } catch (error) {
            console.error('Error loading waste report:', error);
        }
    };

    // Load production options for dropdown
    const loadProductionOptions = async () => {
        try {
            const result = await manufacturingActions.getProductions({ limit: 1000 });
            if (result.success) {
                setProductionOptions(result.data.map(p => ({
                    value: p.id,
                    label: `${p.production_no} - ${p.batch_no} (${formatDate(p.production_date)})`
                })));
            }
        } catch (error) {
            console.error('Error loading production options:', error);
        }
    };

    useEffect(() => {
        if (canView) {
            loadAllData();
            loadProductionOptions();
        }
    }, [filters.from_date, filters.to_date, filters.production_id]);

    // Handle filter change
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Apply filters
    const applyFilters = () => {
        loadAllData();
    };

    // Format date
    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const statusMap = {
            completed: <span className="badge bg-success">COMPLETED</span>,
            pending: <span className="badge bg-warning">PENDING</span>,
            draft: <span className="badge bg-secondary">DRAFT</span>,
            cancelled: <span className="badge bg-danger">CANCELLED</span>
        };
        return statusMap[status] || <span className="badge bg-secondary">{status?.toUpperCase() || 'N/A'}</span>;
    };

    // Get waste type badge
    const getWasteTypeBadge = (type) => {
        if (type === 'raw_material') {
            return <span className="badge bg-info">Raw Material</span>;
        } else if (type === 'finished_good') {
            return <span className="badge bg-warning">Finished Good</span>;
        }
        return <span className="badge bg-secondary">Unknown</span>;
    };

    // Tooltip renderer
    const renderTooltip = (props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    );

    // Calculate totals
    const productionTotals = {
        total_quantity: productionReport.reduce((sum, p) => sum + (parseFloat(p.total_quantity) || 0), 0),
        total_cost: productionReport.reduce((sum, p) => sum + (parseFloat(p.total_cost) || 0), 0)
    };

    const wasteTotals = {
        total_cost: wasteReport.reduce((sum, w) => sum + (parseFloat(w.cost) || 0), 0),
        total_quantity: wasteReport.reduce((sum, w) => sum + (parseFloat(w.quantity) || 0), 0)
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Manufacturing Reports</h4>
                        <h6>View and export manufacturing reports</h6>
                    </div>

                    <ul className="table-top-head">

                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <Link onClick={loadAllData}>
                                    <RotateCcw />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <Link
                                    className={data ? "active" : ""}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp />
                                </Link>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>

                {/* Filters */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-lg-3">
                                <div className="mb-3">
                                    <label>From Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filters.from_date}
                                        onChange={(e) => handleFilterChange('from_date', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-3">
                                <div className="mb-3">
                                    <label>To Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filters.to_date}
                                        onChange={(e) => handleFilterChange('to_date', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-4">
                                <div className="mb-3">
                                    <label>Production (Optional)</label>
                                    <Select
                                        options={productionOptions}
                                        isClearable
                                        placeholder="All Productions"
                                        onChange={(selected) => handleFilterChange('production_id', selected?.value || '')}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <div className="mb-3">
                                    <label>&nbsp;</label>
                                    <button className="btn btn-primary w-100" onClick={applyFilters}>
                                        <Search size={16} className="me-2" />
                                        Apply Filters
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <ul className="nav nav-tabs mb-4">
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'summary' ? 'active' : ''}`}
                            onClick={() => setActiveTab('summary')}
                        >
                            <BarChart2 size={16} className="me-2" />
                            Summary
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'production' ? 'active' : ''}`}
                            onClick={() => setActiveTab('production')}
                        >
                            <Package size={16} className="me-2" />
                            Production Report
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'waste' ? 'active' : ''}`}
                            onClick={() => setActiveTab('waste')}
                        >
                            <AlertTriangle size={16} className="me-2" />
                            Waste Report
                        </button>
                    </li>
                </ul>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Summary Tab */}
                        {activeTab === 'summary' && (
                            <div>
                                {/* Key Metrics Cards */}
                                <div className="row">
                                    <div className="col-lg-3 col-md-6 col-sm-12">
                                        <div className="card">
                                            <div className="card-body">
                                                <div className="d-flex align-items-center">
                                                    <div className="bg-primary bg-opacity-10 p-3 rounded me-3">
                                                        <Package size={24} className="text-primary" />
                                                    </div>
                                                    <div>
                                                        <p className="text-muted mb-1">Total Productions</p>
                                                        <h3 className="mb-0">{summary.total_productions}</h3>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-md-6 col-sm-12">
                                        <div className="card">
                                            <div className="card-body">
                                                <div className="d-flex align-items-center">
                                                    <div className="bg-success bg-opacity-10 p-3 rounded me-3">
                                                        <TrendingUp size={24} className="text-success" />
                                                    </div>
                                                    <div>
                                                        <p className="text-muted mb-1">Total Units Produced</p>
                                                        <h3 className="mb-0">{summary.total_units_produced.toLocaleString()}</h3>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-md-6 col-sm-12">
                                        <div className="card">
                                            <div className="card-body">
                                                <div className="d-flex align-items-center">
                                                    <div className="bg-warning bg-opacity-10 p-3 rounded me-3">
                                                        <DollarSign size={24} className="text-warning" />
                                                    </div>
                                                    <div>
                                                        <p className="text-muted mb-1">Total Production Cost</p>
                                                        <h3 className="mb-0">{formatCurrency(summary.total_production_cost)}</h3>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-lg-3 col-md-6 col-sm-12">
                                        <div className="card">
                                            <div className="card-body">
                                                <div className="d-flex align-items-center">
                                                    <div className="bg-danger bg-opacity-10 p-3 rounded me-3">
                                                        <AlertTriangle size={24} className="text-danger" />
                                                    </div>
                                                    <div>
                                                        <p className="text-muted mb-1">Total Waste Cost</p>
                                                        <h3 className="mb-0">{formatCurrency(summary.total_waste_cost)}</h3>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Production vs Waste Chart */}
                                <div className="row mt-4">
                                    <div className="col-md-6">
                                        <div className="card">
                                            <div className="card-header">
                                                <h5 className="mb-0">Production vs Waste</h5>
                                            </div>
                                            <div className="card-body text-center">
                                                <div className="py-4">
                                                    <PieChart size={64} className="text-muted mb-3" />
                                                    <div className="row">
                                                        <div className="col-6">
                                                            <div className="border-end">
                                                                <h4 className="text-success">{summary.total_units_produced.toLocaleString()}</h4>
                                                                <p className="text-muted">Units Produced</p>
                                                            </div>
                                                        </div>
                                                        <div className="col-6">
                                                            <h4 className="text-danger">{summary.total_waste_quantity.toLocaleString()}</h4>
                                                            <p className="text-muted">Waste Units</p>
                                                        </div>
                                                    </div>
                                                    <div className="mt-3">
                                                        <div className="progress" style={{ height: '25px' }}>
                                                            <div
                                                                className="progress-bar bg-success"
                                                                style={{ width: `${100 - summary.waste_percentage}%` }}
                                                            >
                                                                Production: {100 - summary.waste_percentage}%
                                                            </div>
                                                            <div
                                                                className="progress-bar bg-danger"
                                                                style={{ width: `${summary.waste_percentage}%` }}
                                                            >
                                                                Waste: {summary.waste_percentage}%
                                                            </div>
                                                        </div>
                                                        <p className="text-info mt-2">
                                                            Waste Percentage: {summary.waste_percentage}%
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="card">
                                            <div className="card-header">
                                                <h5 className="mb-0">Cost Analysis</h5>
                                            </div>
                                            <div className="card-body">
                                                <div className="py-3">
                                                    <div className="mb-4">
                                                        <label className="fw-bold">Raw Material Cost</label>
                                                        <div className="progress mb-2" style={{ height: '20px' }}>
                                                            <div
                                                                className="progress-bar bg-primary"
                                                                style={{ width: `${summary.total_production_cost > 0 ? (summary.total_raw_material_cost / summary.total_production_cost) * 100 : 0}%` }}
                                                            >
                                                                {summary.total_production_cost > 0 ? ((summary.total_raw_material_cost / summary.total_production_cost) * 100).toFixed(1) : 0}%
                                                            </div>
                                                        </div>
                                                        <small className="text-muted">{formatCurrency(summary.total_raw_material_cost)}</small>
                                                    </div>
                                                    <div className="mb-4">
                                                        <label className="fw-bold">Production Value</label>
                                                        <div className="progress mb-2" style={{ height: '20px' }}>
                                                            <div
                                                                className="progress-bar bg-success"
                                                                style={{ width: `100%` }}
                                                            >
                                                                100%
                                                            </div>
                                                        </div>
                                                        <small className="text-muted">{formatCurrency(summary.total_production_cost)}</small>
                                                    </div>
                                                    <div className="mb-4">
                                                        <label className="fw-bold">Waste Cost</label>
                                                        <div className="progress mb-2" style={{ height: '20px' }}>
                                                            <div
                                                                className="progress-bar bg-danger"
                                                                style={{ width: `${summary.total_production_cost > 0 ? (summary.total_waste_cost / summary.total_production_cost) * 100 : 0}%` }}
                                                            >
                                                                {summary.total_production_cost > 0 ? ((summary.total_waste_cost / summary.total_production_cost) * 100).toFixed(1) : 0}%
                                                            </div>
                                                        </div>
                                                        <small className="text-muted">{formatCurrency(summary.total_waste_cost)}</small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Production Report Tab */}
                        {activeTab === 'production' && (
                            <div className="card">
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table className="table table-hover">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Production No</th>
                                                    <th>Batch No</th>
                                                    <th>Date</th>
                                                    <th>Raw Materials</th>
                                                    <th>Finished Goods</th>
                                                    <th>Total Qty</th>
                                                    <th>Total Cost</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {productionReport.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="8" className="text-center py-5">
                                                            <Package size={48} className="text-muted mb-3" />
                                                            <h5>No production data found</h5>
                                                            <p className="text-muted">No productions found for the selected period</p>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    productionReport.map((prod) => (
                                                        <tr key={prod.id}>
                                                            <td>
                                                                <strong>{prod.production_no}</strong>
                                                                {prod.purchase_invoice_no && (
                                                                    <small className="d-block text-muted">PO: {prod.purchase_invoice_no}</small>
                                                                )}
                                                            </td>
                                                            <td><code>{prod.batch_no}</code></td>
                                                            <td>{formatDate(prod.production_date)}</td>
                                                            <td>{prod.raw_materials_count || 0} items</td>
                                                            <td>{prod.finished_goods_count || 0} items</td>
                                                            <td>{parseFloat(prod.total_quantity || 0).toLocaleString()} units</td>
                                                            <td className="fw-bold text-success">{formatCurrency(prod.total_cost)}</td>
                                                            <td>{getStatusBadge(prod.status)}</td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                            {productionReport.length > 0 && (
                                                <tfoot className="table-light">
                                                    <tr>
                                                        <td colSpan="5" className="text-end fw-bold">Totals:</td>
                                                        <td className="fw-bold">{productionTotals.total_quantity.toLocaleString()} units</td>
                                                        <td className="fw-bold text-success">{formatCurrency(productionTotals.total_cost)}</td>
                                                        <td />
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Waste Report Tab */}
                        {activeTab === 'waste' && (
                            <div className="card">
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table className="table table-hover">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Waste No</th>
                                                    <th>Date</th>
                                                    <th>Production No</th>
                                                    <th>Batch No</th>
                                                    <th>Product</th>
                                                    <th>Type</th>
                                                    <th>Quantity</th>
                                                    <th>Reason</th>
                                                    <th>Cost</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {wasteReport.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="9" className="text-center py-5">
                                                            <AlertTriangle size={48} className="text-muted mb-3" />
                                                            <h5>No waste data found</h5>
                                                            <p className="text-muted">No waste records found for the selected period</p>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    wasteReport.map((waste) => (
                                                        <tr key={waste.id}>
                                                            <td><strong>{waste.waste_no}</strong></td>
                                                            <td>{formatDate(waste.waste_date)}</td>
                                                            <td>{waste.production_no}</td>
                                                            <td><code>{waste.batch_no}</code></td>
                                                            <td>{waste.product_name}</td>
                                                            <td>{getWasteTypeBadge(waste.wastage_type || waste.waste_type)}</td>
                                                            <td><span className="text-danger fw-bold">{parseFloat(waste.quantity).toLocaleString()} {waste.unit}</span></td>
                                                            <td>{waste.reason || '—'}</td>
                                                            <td className="text-danger fw-bold">{formatCurrency(waste.cost)}</td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                            {wasteReport.length > 0 && (
                                                <tfoot className="table-light">
                                                    <tr>
                                                        <td colSpan="7" className="text-end fw-bold">Total Waste Cost:</td>
                                                        <td colSpan="2" className="fw-bold text-danger">{formatCurrency(wasteTotals.total_cost)}</td>
                                                    </tr>
                                                </tfoot>
                                            )}
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ManufacturingReports;